import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { CarCard } from "@/components/site/CarCard";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { InnerPageRequirements } from "@/components/site/InnerPageRequirements";
import { ReviewsHighlight } from "@/components/site/ReviewsHighlight";
import { cars } from "@/lib/cars";
import { Phone, MapPin, Check } from "lucide-react";
import {
  breadcrumbScript,
} from "@/lib/schema";


export const Route = createFileRoute("/luxury-car-rental-vancouver")({
  component: LuxuryLanding,
  head: () => ({
    meta: [
      { title: "Luxury Car Rental Vancouver from $200/Day | Avolus Cars" },
      { name: "description", content: "Luxury car rental in Vancouver from $200/day. Explore the 2024 Range Rover P530 LWB, Mercedes S-Class, BMW, Porsche and Cadillac fleet." },
      { property: "og:title", content: "Luxury Car Rental Vancouver from $200/Day | Avolus Cars" },
      { property: "og:description", content: "Luxury car rental in Vancouver from $200/day. Free delivery within Vancouver and to YVR Airport; other locations by arrangement." },

      { property: "og:url", content: "https://avoluscars.com/luxury-car-rental-vancouver" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/luxury-car-rental-vancouver" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is the best luxury car rental company in Vancouver?",
              acceptedAnswer: {
                "@type": "Answer",
                 text: "Avolus Cars offers Range Rover, Mercedes-Benz, BMW, Porsche and Cadillac rentals in Vancouver. The 2024 Range Rover P530 LWB is available from $395/day. See the fleet for photos, specifications and current rates.",
              },
            },
            {
              "@type": "Question",
              name: "How much does it cost to rent a luxury car in Vancouver?",
              acceptedAnswer: {
                "@type": "Answer",
                 text: "The 2024 Range Rover P530 LWB starts at $395/day, the same as the Cadillac Escalade and Mercedes-Benz S580. Its 48-hour rate is $295/day, 3-6 days are $275/day and 7+ days are $250/day. See the full rate card for every vehicle.",
              },
            },
            {
              "@type": "Question",
              name: "Do you deliver luxury rentals to Vancouver International Airport (YVR)?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Avolus Cars delivers luxury vehicles free within Vancouver and to YVR Airport. Other Metro Vancouver locations, Whistler, and the Fraser Valley are available by arrangement; contact us to confirm delivery pricing.",
              },
            },
            {
              "@type": "Question",
              name: "Can I rent a luxury car in Vancouver for a wedding?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. The Mercedes-Benz S-Class and Cadillac Escalade are popular wedding vehicles. All cars are unbranded (no decals or rental plates) and delivered to the ceremony or reception venue. Minimum driver age is 25 with a full unrestricted license.",
              },
            },
            {
              "@type": "Question",
              name: "What are the requirements to rent a luxury car in Vancouver?",
              acceptedAnswer: {
                "@type": "Answer",
                 text: "Drivers must be 25 or older with a full unrestricted driver's license (no N or L class). Every vehicle has a refundable $2,000 CAD security deposit held on a credit card, and LDW is $60 per day. Payment accepted: Visa, Mastercard, American Express, PayPal, Interac e-Transfer, cash, Bitcoin, and Ethereum.",
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Luxury Car Rental",
          provider: { "@id": "https://avoluscars.com/#business" },
          areaServed: { "@type": "City", name: "Vancouver, British Columbia" },
          offers: { "@type": "AggregateOffer", priceCurrency: "CAD", lowPrice: 200, highPrice: 395 },
        }),
      },
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Luxury Car Rental Vancouver", url: "https://avoluscars.com/luxury-car-rental-vancouver" },
      ]),
    ],
  }),
});

const reasons = [
  "Hand-picked fleet: no dated rentals, no high-mileage cars",
  "Free delivery within Vancouver and to YVR Airport",
  "$5,000,000 third-party liability included on every rental",
  "Unbranded vehicles: no decals, no rental plates",
  "Credit card, PayPal, e-Transfer, cash, and crypto accepted",
];

const areas = ["Downtown Vancouver", "YVR Airport", "Richmond", "Burnaby", "West Vancouver", "North Vancouver", "Surrey", "Whistler", "Fraser Valley"];

function LuxuryLanding() {
  return (
    <Layout inner>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Vancouver Luxury Car Rental</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-4xl leading-[1.05]">
              Luxury Car Rental in Vancouver.
            </h1>
            <nav aria-label="On this page" className="md:hidden mt-4 flex flex-wrap gap-x-5 text-sm underline underline-offset-4"><a href="#vehicles" className="inline-flex items-center min-h-11">Vehicles</a><a href="#questions" className="inline-flex items-center min-h-11">Questions</a><a href="/rates" className="inline-flex items-center min-h-11">Rates</a></nav>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Avolus Cars offers luxury car rental in Vancouver from $200 CAD per day for 24 hours, with free delivery within Vancouver and to YVR. Choose from eight 2021-2024 vehicles, with $5,000,000 third-party liability included. Rates are before taxes and mandatory $60/day LDW; every vehicle has a $2,000 refundable security deposit.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="/book"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Book Now
              </a>
              <a
                href="tel:+17783798881"
                className="md:hidden inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                <Phone size={14} /> 778-379-8881
              </a>
              <a
                href="/contact"
                className="hidden md:inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] px-8 py-4 border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Contact Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LLM-quotable answer block: short, factual, scannable */}
      <section className="py-16 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl text-foreground mb-6">
              About Avolus Cars
            </h2>
            <p className="text-foreground/85 leading-relaxed">
              Avolus Cars is a Vancouver-based luxury and exotic car rental service founded to offer
              a curated alternative to traditional rental fleets. We operate without a physical
              storefront: every vehicle is delivered directly to the customer across Metro
              Vancouver, YVR Airport, the Fraser Valley, and Whistler. Daily rentals start at $200
               CAD and our fleet includes the Range Rover P530 LWB, Mercedes-Benz S-Class, BMW M3 Competition, BMW X7,
               BMW M440i, Porsche Macan, Cadillac Escalade and BMW X1.
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Founded</dt>
                <dd className="text-foreground mt-1">Vancouver, British Columbia</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Fleet size</dt>
                 <dd className="text-foreground mt-1">{cars.length} vehicles, model years 2021-2024</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Daily rates</dt>
                 <dd className="text-foreground mt-1">$200-$395 CAD for 24 hours</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Minimum age</dt>
                <dd className="text-foreground mt-1">25 years, full license</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Insurance included</dt>
                <dd className="text-foreground mt-1">$5,000,000 third-party liability</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Payment accepted</dt>
                <dd className="text-foreground mt-1">Card, PayPal, e-Transfer, cash, crypto</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Contact</dt>
                <dd className="text-foreground mt-1">778-379-8881</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</dt>
                <dd className="text-foreground mt-1">info@avoluscars.com</dd>
              </div>
            </dl>
          </Reveal>
        </div>
      </section>

      <section id="vehicles" className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>The Fleet</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl text-foreground max-w-2xl mb-12">
               Eight vehicles. Zero compromises.
            </h2>
          </Reveal>
          <div className="compact-fleet grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {cars.map((c, i) => (
              <CarCard key={c.slug} car={c} index={i} compact />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-border" id="choose">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <h2 className="font-display text-3xl md:text-4xl mb-6">Which luxury car should I rent in Vancouver?</h2>
          <ul className="space-y-4 text-muted-foreground">
            <li>Airport families: <Link to="/fleet/$slug" params={{slug:"cadillac-escalade-2023"}} className="underline">Escalade</Link> or <Link to="/fleet/$slug" params={{slug:"bmw-x7-m60i-2024"}} className="underline">X7</Link>, with three rows. Confirm passenger and luggage space before booking.</li>
            <li>Executive travel: <Link to="/fleet/$slug" params={{slug:"mercedes-s-class-2022"}} className="underline">S580</Link> or <Link to="/fleet/$slug" params={{slug:"range-rover-p530-lwb-2024"}} className="underline">Range Rover</Link> for a quiet cabin and refined arrival.</li>
            <li>Winter Whistler trips: <Link to="/fleet/$slug" params={{slug:"cadillac-escalade-2023"}} className="underline">Escalade</Link>, the only vehicle currently fitted with winter tires. Confirm road conditions and suitability before booking.</li>
            <li>City and value: <Link to="/fleet/$slug" params={{slug:"bmw-x1-2023"}} className="underline">X1</Link> for a compact footprint and lower multi-day rates.</li>
          </ul>
        </div>
      </section>

      {/* Fact-density Q&A block with table: engineered for LLM passage extraction */}
      <section className="py-16 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl text-foreground mb-4">
              How much does it cost to rent a luxury car in Vancouver?
            </h2>
            <p className="text-foreground/85 leading-relaxed">
              The Avolus fleet starts at $200 CAD per day for a 24-hour rental and reaches $395
              per day for the Range Rover P530 LWB, Escalade and S580. Avolus Cars rents
              luxury sedans, performance coupes, and full-size luxury SUVs with free delivery
              within Vancouver and to YVR Airport. Other delivery locations are by arrangement. All rentals include $5,000,000 third-party
              liability insurance and 150 km of daily mileage. Mandatory LDW is an additional $60 per day.
            </p>
            <div className="hidden md:block mt-8 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-3 pr-4 text-xs uppercase tracking-[0.2em] text-muted-foreground font-normal">Vehicle</th>
                    <th className="py-3 pr-4 text-xs uppercase tracking-[0.2em] text-muted-foreground font-normal">Category</th>
                    <th className="py-3 pr-4 text-xs uppercase tracking-[0.2em] text-muted-foreground font-normal">Daily rate (CAD)</th>
                    <th className="py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground font-normal">Security deposit</th>
                  </tr>
                </thead>
                <tbody className="text-foreground/85">
                   {cars.map((car) => (
                     <tr key={car.slug} className="border-b border-border/60">
                       <td className="py-3 pr-4"><Link to="/fleet/$slug" params={{ slug: car.slug }} className="underline underline-offset-4">{car.name}</Link></td>
                       <td className="py-3 pr-4">{car.category}</td>
                       <td className="py-3 pr-4">${car.pricePerDay}</td>
                       <td className="py-3">{car.rates.security}</td>
                     </tr>
                   ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Rates shown are starting daily rates. Multi-day, weekend, and weekly discounts
              available. Minimum driver age 25 with a full unrestricted license.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Visible FAQ: mirrors FAQPage JSON-LD for Google/LLM extraction */}
      <section id="questions" className="py-20 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl text-foreground mb-12">
              Frequently asked questions.
            </h2>
            <div className="md:hidden divide-y divide-border"><details><summary className="font-display text-lg py-4 cursor-pointer">What is the best luxury car rental company in Vancouver?</summary><p className="pb-5 text-muted-foreground">Avolus Cars offers Range Rover, Mercedes-Benz, BMW, Porsche and Cadillac rentals in Vancouver. The 2024 Range Rover P530 LWB is available from $395/day. See the fleet for photos, specifications and current rates.</p></details><details><summary className="font-display text-lg py-4 cursor-pointer">How much does it cost to rent a luxury car in Vancouver?</summary><p className="pb-5 text-muted-foreground">The 2024 Range Rover P530 LWB starts at $395/day, the same as the Cadillac Escalade and Mercedes-Benz S580. Its 48-hour rate is $295/day, 3-6 days are $275/day and 7+ days are $250/day. See the full rate card for every vehicle.</p></details><details><summary className="font-display text-lg py-4 cursor-pointer">Do you deliver luxury rentals to Vancouver International Airport (YVR)?</summary><p className="pb-5 text-muted-foreground">Yes. Avolus Cars delivers luxury vehicles free within Vancouver and to YVR Airport. Other Metro Vancouver locations, Whistler, and the Fraser Valley are available by arrangement; contact us to confirm delivery pricing.</p></details><details><summary className="font-display text-lg py-4 cursor-pointer">Can I rent a luxury car in Vancouver for a wedding?</summary><p className="pb-5 text-muted-foreground">Yes. The Mercedes-Benz S-Class and Cadillac Escalade are popular wedding vehicles. All cars are unbranded (no decals or rental plates) and delivered to the ceremony or reception venue. Minimum driver age is 25 with a full unrestricted license.</p></details><details><summary className="font-display text-lg py-4 cursor-pointer">What are the requirements to rent a luxury car in Vancouver?</summary><p className="pb-5 text-muted-foreground">Drivers must be 25 or older with a full unrestricted driver's license (no N or L class). Every vehicle has a refundable $2,000 CAD security deposit held on a credit card, and LDW is $60 per day. Payment accepted: Visa, Mastercard, American Express, PayPal, Interac e-Transfer, cash, Bitcoin, and Ethereum.</p></details></div>
            <dl className="hidden md:block space-y-10">
              <div>
                <dt className="font-display text-xl text-foreground mb-3">What is the best luxury car rental company in Vancouver?</dt>
               <dd className="text-foreground/85 leading-relaxed">Avolus Cars offers Range Rover, Mercedes-Benz, BMW, Porsche and Cadillac rentals in Vancouver. The 2024 Range Rover P530 LWB is available from $395/day. See the fleet for photos, specifications and current rates.</dd>
              </div>
              <div>
                <dt className="font-display text-xl text-foreground mb-3">How much does it cost to rent a luxury car in Vancouver?</dt>
                <dd className="text-foreground/85 leading-relaxed">The 2024 Range Rover P530 LWB starts at $395/day, the same as the Cadillac Escalade and Mercedes-Benz S580. Its 48-hour rate is $295/day, 3-6 days are $275/day and 7+ days are $250/day. See the full rate card for every vehicle.</dd>
              </div>
              <div>
                <dt className="font-display text-xl text-foreground mb-3">Do you deliver luxury rentals to Vancouver International Airport (YVR)?</dt>
                <dd className="text-foreground/85 leading-relaxed">Yes. Avolus Cars delivers luxury vehicles free within Vancouver and to YVR Airport. Other Metro Vancouver locations, Whistler, and the Fraser Valley are available by arrangement; contact us to confirm delivery pricing.</dd>
              </div>
              <div>
                <dt className="font-display text-xl text-foreground mb-3">Can I rent a luxury car in Vancouver for a wedding?</dt>
                <dd className="text-foreground/85 leading-relaxed">Yes. The Mercedes-Benz S-Class and Cadillac Escalade are popular wedding vehicles. All cars are unbranded (no decals or rental plates) and delivered to the ceremony or reception venue. Minimum driver age is 25 with a full unrestricted license.</dd>
              </div>
              <div>
                <dt className="font-display text-xl text-foreground mb-3">What are the requirements to rent a luxury car in Vancouver?</dt>
                <dd className="text-foreground/85 leading-relaxed">Drivers must be 25 or older with a full unrestricted driver's license (no N or L class). Every vehicle has a refundable $2,000 CAD security deposit held on a credit card, and LDW is $60 per day. Payment accepted: Visa, Mastercard, American Express, PayPal, Interac e-Transfer, cash, Bitcoin, and Ethereum.</dd>
              </div>
            </dl>
          </Reveal>
        </div>
      </section>



      <section className="py-20 lg:py-28 bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-16 lg:grid-cols-2">

          <Reveal>
            <Eyebrow>Why Avolus</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl text-foreground mb-8">
              The difference is in the details.
            </h2>
            <ul className="space-y-4">
              {reasons.map((r) => (
                <li key={r} className="flex gap-3 text-foreground/85">
                  <Check size={20} className="shrink-0 mt-1 text-foreground" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Delivery Areas</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl text-foreground mb-8">
              We come to you.
            </h2>
            <ul className="grid grid-cols-2 gap-3">
              {areas.map((a) => (
                <li key={a} className="flex gap-2 items-center text-foreground/85">
                  <MapPin size={16} className="text-foreground/60" /> {a}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-muted-foreground">
              Delivering further out? Just ask. We regularly drop cars in Kelowna, Vancouver Island, and the Okanagan.
            </p>
          </Reveal>
        </div>
      </section>

      <InnerPageRequirements />

      <section className="hidden md:block py-24 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl text-foreground">
              Ready to drive?
            </h2>
            <p className="mt-6 text-foreground/70 max-w-xl mx-auto">
              Book a luxury rental in Vancouver in minutes, with instant confirmation online.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="/book"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Book Now
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <ReviewsHighlight eyebrow="Luxury Rental Reviews" />
    </Layout>
  );
}
